/**
 * LogService - centralized logging with configurable destinations
 *
 * Singleton that routes logs to console, UI overlay, or file.
 * Replaces errorStore - all logging goes through here.
 */

import { LogLevel } from '../nodes/INode';
import { isBrowser } from '../runtime/env';
import { config } from '../config';

export type LogDestination = 'console' | 'overlay' | 'file';

export interface LogEntry {
  level: LogLevel;
  source: string;
  message: string;
  context?: string;
  timestamp: number;
  stack?: string;
}

type LogListener = (entry: LogEntry) => void;

class LogServiceImpl {
  private destinations: Set<LogDestination> = new Set(['console', 'overlay', 'file']);
  private listeners: Set<LogListener> = new Set();
  private entries: LogEntry[] = [];
  private maxEntries = 100;
  private logLevel: LogLevel = LogLevel.Trace;

  /**
   * Set global log level - logs below this level are ignored
   */
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  /**
   * Enable a log destination
   */
  enableDestination(dest: LogDestination): void {
    this.destinations.add(dest);
  }

  /**
   * Disable a log destination
   */
  disableDestination(dest: LogDestination): void {
    this.destinations.delete(dest);
  }

  /**
   * Check if a destination is enabled
   */
  isDestinationEnabled(dest: LogDestination): boolean {
    return this.destinations.has(dest);
  }

  /**
   * Subscribe to log entries (for UI overlay)
   */
  subscribe(listener: LogListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Log a message at the specified level
   */
  log(level: LogLevel, message: string, source: string, context?: string): void {
    // Node logs (have context = nodeId) bypass global filter - they have their own node-level filter
    // Non-node logs are filtered by global level
    if (!context && !this.shouldLog(level)) return;

    // Capture stack trace for errors
    let stack: string | undefined;
    if (level === LogLevel.Error) {
      const err = new Error();
      stack = err.stack?.split('\n').slice(3).join('\n'); // Skip log() frames
    }

    const entry: LogEntry = {
      level,
      source,
      message,
      context,
      timestamp: Date.now(),
      stack
    };

    // Store entry with priority-based eviction
    // When full, evict lowest priority (trace > info > error) to preserve errors
    this.entries.push(entry);
    if (this.entries.length > this.maxEntries) {
      this.evictLowestPriority();
    }

    // Route to enabled destinations
    if (this.destinations.has('console')) {
      this.logToConsole(entry);
    }

    if (this.destinations.has('overlay')) {
      this.notifyListeners(entry);
    }

    if (this.destinations.has('file')) {
      this.logToFile(entry);
    }
  }

  /** Log error */
  error(source: string, ...args: any[]): void {
    const message = args.map(a => typeof a === 'string' ? a : JSON.stringify(a)).join(' ');
    this.log(LogLevel.Error, message, source);
  }

  /** Log info */
  info(source: string, ...args: any[]): void {
    const message = args.map(a => typeof a === 'string' ? a : JSON.stringify(a)).join(' ');
    this.log(LogLevel.Info, message, source);
  }

  /** Log trace */
  trace(source: string, ...args: any[]): void {
    const message = args.map(a => typeof a === 'string' ? a : JSON.stringify(a)).join(' ');
    this.log(LogLevel.Trace, message, source);
  }

  /**
   * Get all log entries
   */
  getAll(): LogEntry[] {
    return [...this.entries];
  }

  /**
   * Get entries filtered by source
   */
  getBySource(source: string): LogEntry[] {
    return this.entries.filter(e => e.source === source);
  }

  /**
   * Clear all entries
   */
  clear(): void {
    this.entries = [];
  }

  /**
   * Clear entries by source
   */
  clearBySource(source: string): void {
    this.entries = this.entries.filter(e => e.source !== source);
  }

  /**
   * Clear entries by context (e.g., node ID)
   */
  clearByContext(context: string): void {
    this.entries = this.entries.filter(e => e.context !== context);
  }

  /**
   * Clear only global logs (those without context)
   * Preserves node logs which are managed per-node
   */
  clearGlobal(): void {
    this.entries = this.entries.filter(e => e.context);
  }

  private shouldLog(level: LogLevel): boolean {
    return this.levelPriority(level) <= this.levelPriority(this.logLevel);
  }

  private levelPriority(level: LogLevel): number {
    const priority: Record<LogLevel, number> = {
      [LogLevel.Error]: 1,
      [LogLevel.Info]: 2,
      [LogLevel.Trace]: 3
    };
    return priority[level];
  }

  /**
   * Evict the oldest entry with lowest priority.
   * Prioritizes keeping errors over infos over traces.
   */
  private evictLowestPriority(): void {
    // Find oldest trace, then oldest info, then oldest error
    for (const targetLevel of [LogLevel.Trace, LogLevel.Info, LogLevel.Error]) {
      const idx = this.entries.findIndex(e => e.level === targetLevel);
      if (idx !== -1) {
        this.entries.splice(idx, 1);
        return;
      }
    }
    // Fallback: remove oldest (shouldn't happen)
    this.entries.shift();
  }

  private logToConsole(entry: LogEntry): void {
    const prefix = entry.context
      ? `[${entry.source}] ${entry.context}:`
      : `[${entry.source}]`;

    switch (entry.level) {
      case LogLevel.Error:
        console.error(prefix, entry.message);
        break;
      case LogLevel.Info:
        console.info(prefix, entry.message);
        break;
      case LogLevel.Trace:
        console.log(prefix, entry.message);
        break;
    }
  }

  private notifyListeners(entry: LogEntry): void {
    for (const listener of this.listeners) {
      listener(entry);
    }
  }

  private logToFile(entry: LogEntry): void {
    // In Node.js, write directly to file
    if (!isBrowser) {
      this.writeToFileSync(entry);
      return;
    }

    // In browser, POST to server API
    const payload = JSON.stringify({ entry });
    fetch(`${config.apiUrl}/api/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload
    }).catch(() => {});
  }

  private logFileRotated = false;

  private writeToFileSync(entry: LogEntry): void {
    try {
      // Dynamic import for Node.js environment
      const fs = require('fs');
      const path = require('path');
      const logDir = path.join(process.cwd(), 'files', 'logs');
      const logFile = path.join(logDir, 'app.log');
      const oldLogFile = path.join(logDir, 'app.log.old');

      if (!fs.existsSync(logDir)) {
        fs.mkdirSync(logDir, { recursive: true });
      }

      // Rotate log file on first write each session (server startup)
      if (!this.logFileRotated && fs.existsSync(logFile)) {
        // Move current to .old (overwrites previous .old)
        fs.renameSync(logFile, oldLogFile);
        this.logFileRotated = true;
      }
      this.logFileRotated = true;

      const timestamp = new Date(entry.timestamp).toISOString();

      // ANSI colors: bright red for errors, reset after
      const RED = '\x1b[91m';
      const RESET = '\x1b[0m';
      const color = entry.level === LogLevel.Error ? RED : '';
      const reset = entry.level === LogLevel.Error ? RESET : '';

      let line = `${color}${timestamp} [${entry.level}] [${entry.source}] ${entry.message}${reset}`;
      if (entry.stack) {
        line += '\n' + color + entry.stack + reset;
      }
      line += '\n';

      fs.appendFileSync(logFile, line);
    } catch {
      // Silently ignore file write errors
    }
  }
}

/** Singleton logger instance */
export const logger = new LogServiceImpl();
